import React, { useState, useEffect } from "react";
import {
  User,
  Bot,
  Download,
  Plus,
  CodeXml,
  ArrowDownToLine,
  ThumbsDown,
  ThumbsUp,
  Table,
  ChartArea,
  LineChart,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import {
  // downloadCSV,
  downloadCollectionsCSV,
  sendFeedbackAPI,
} from "./ActionCreator";

import {
  ProcessingSteps,
  dummyIntermediateSteps,
} from "./SerchAnimation";

import Tooltip from "@mui/material/Tooltip";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TableChart from "./TableChart";

const tooltipStyle = {
  position: "absolute",
  bottom: "130%",
  left: "50%",
  transform: "translateX(-50%)",
  background: "rgba(17,17,17,0.95)",
  color: "#fff",
  fontSize: "11px",
  padding: "4px 8px",
  borderRadius: "6px",
  whiteSpace: "nowrap",
  zIndex: 9999,
  pointerEvents: "none",
  boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
};

export const ChatMessage = ({
  message,
  showSqlQuery,
  onAddFollowUp,
  onToggleSQL,
  activeSQLMessageId,
  showSQLToggle = true,
  isStreaming = false,
  showProcessingSteps = false,
}) => {
  const isUser = message.sender === "user";

  const [displayedText, setDisplayedText] = useState("");
  const [isStreamComplete, setIsStreamComplete] = useState(!isStreaming);
  const [hasShownSteps, setHasShownSteps] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [showSubmitAlert, setShowSubmitAlert] = useState(false);

  const [viewMode, setViewMode] = useState("table");

  useEffect(() => {
    setDisplayedText(message.text || message.content || "");
    setIsStreamComplete(true);
  }, [message.text, message.content]);

  const schema = {
    ...defaultSchema,
    tagNames: [
      ...(defaultSchema.tagNames || []),
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
    ],
  };

  const handleDownload = () => {
    if (message.download_link) {
      const parts = message.download_link.split("/");
      const fileName = parts[parts.length - 1];
      downloadCollectionsCSV(fileName);
    }
  };

  const handleAddFollowUp = () => {
    if (!message.download_link || !onAddFollowUp) return;
    const parts = message.download_link.split("/");
    const fileName = parts[parts.length - 1];
    onAddFollowUp(fileName, message.download_link);
  };

  const handleSubmitFeedback = async () => {
    try {
      setSubmitting(true);

      await sendFeedbackAPI({
        queryId: message.query_id,
        thumbsUp: feedbackType === "up",
        message:
          feedbackText ||
          (feedbackType === "up" ? "Good response" : "Needs improvement"),
        userId: "SuperUser",
      });

      setFeedbackSubmitted(true);
      setShowFeedbackModal(false);
      setFeedbackText("");
      setFeedbackType(null);

      setShowSubmitAlert(true);

      setTimeout(() => {
        setShowSubmitAlert(false);
      }, 2000);
    } catch (err) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {showSubmitAlert && (
        <div className="feedback-alert-overlay">
          <div className="feedback-alert-box">
            <div className="feedback-alert-icon">👍</div>

            <div className="feedback-alert-title">Feedback Submitted</div>

            <div className="feedback-alert-text">
              Thank you for helping us improve.
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        maxWidth="xs"
        fullWidth
        disablePortal
        sx={{ zIndex: 20000 }}
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e5e7eb",
            pb: 1,
          }}
        >
          {feedbackType === "up" ? "What did you like?" : "What went wrong?"}
          <IconButton size="small" onClick={() => setShowFeedbackModal(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Write your feedback..."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
          />
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #e5e7eb", px: 3, py: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setShowFeedbackModal(false)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={submitting}
            onClick={handleSubmitFeedback}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <div className={`chat-message ${isUser ? "user" : "bot"}`}>
        <div className={`message-wrapper ${isUser ? "user" : "bot"}`}>
          <div className={`avatar ${isUser ? "user" : "bot"}`}>
            {isUser ? <User /> : <Bot />}
          </div>

          <div className={`message-content ${isUser ? "user" : "bot"}`}>
            {!isUser &&
              message.isProcessing &&
              (!message.intermediateSteps ||
                message.intermediateSteps.length === 0) && (
                <div className="typing-bubble">
                  <svg
                    width="36"
                    height="10"
                    viewBox="0 0 36 10"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="5" cy="5" r="4" fill="#667eea">
                      <animate
                        attributeName="opacity"
                        values="0.3;1;0.3"
                        dur="1.2s"
                        repeatCount="indefinite"
                      />
                    </circle>

                    <circle cx="18" cy="5" r="4" fill="#667eea">
                      <animate
                        attributeName="opacity"
                        values="0.3;1;0.3"
                        dur="1.2s"
                        begin="0.2s"
                        repeatCount="indefinite"
                      />
                    </circle>

                    <circle cx="31" cy="5" r="4" fill="#667eea">
                      <animate
                        attributeName="opacity"
                        values="0.3;1;0.3"
                        dur="1.2s"
                        begin="0.4s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                </div>
              )}
            {!isUser && message.intermediateSteps?.length > 0 && (
              <ProcessingSteps
                steps={message.intermediateSteps}
                isProcessing={message.isProcessing}
                currentStepIndex={message.currentStepIndex ?? 0}
              />
            )}
            {message.type === "mixed" && (
              <div className="mixed-wrapper">
                {displayedText && (
                  <div className="markdown-content">
                    <ReactMarkdown
                      rehypePlugins={[[rehypeRaw], [rehypeSanitize, schema]]}
                    >
                      {displayedText}
                    </ReactMarkdown>
                  </div>
                )}

                {message.table && isStreamComplete && (
                  <div className="table-wrapper clean-table table-with-controls">
                    <div className="table-top-controls">
                      <button
                        className={`table-control-btn ${
                          viewMode === "table" ? "active" : ""
                        }`}
                        onClick={() => setViewMode("table")}
                      >
                        <Table size={18} />
                      </button>

                      <button
                        className={`table-control-btn ${
                          viewMode === "chart" ? "active" : ""
                        }`}
                        onClick={() => setViewMode("chart")}
                      >
                        <ChartArea size={18} />
                      </button>
                      <button
                        className={`table-control-btn ${
                          viewMode === "lineChart" ? "active" : ""
                        }`}
                        onClick={() => setViewMode("lineChart")}
                      >
                        <LineChart size={18} />
                      </button>
                    </div>

                    {viewMode === "table" && (
                      <div className="clean-table-scroll">
                        <table className="clean-table-ui">
                          <thead>
                            <tr>
                              {message.table.columns.map((col, i) => (
                                <th key={i}>{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {message.table.rows.map((row, r) => (
                              <tr key={r}>
                                {row.map((cell, c) => (
                                  <td key={c}>{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {viewMode === "chart" && (
                      <div
                        className="chart-placeholder"
                        style={{
                          minHeight: "400px",
                          height: "400px",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <TableChart
                          key={`chart-${message.id}-${viewMode}`}
                          table={message.table}
                        />
                      </div>
                    )}
                    {viewMode === "lineChart" && (
                      <div
                        className="chart-placeholder"
                        style={{
                          minHeight: "400px",
                          height: "400px",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <TableChart
                          key={`lineChart-${message.id}-${viewMode}`}
                          table={message.table}
                          chartType="line"
                        />
                      </div>
                    )}
                    <div className="d-flex bottom-icon">
                      {!isUser && message.sqlQuery && (
                        <div
                          style={{ position: "relative" }}
                          onMouseEnter={() => setHoveredIcon("sql")}
                          onMouseLeave={() => setHoveredIcon(null)}
                        >
                          <button
                            onClick={() =>
                              onToggleSQL(message.sqlQuery, message.id)
                            }
                          >
                            <CodeXml size={15} />
                          </button>

                          {hoveredIcon === "sql" && (
                            <div style={tooltipStyle}>Show SQL</div>
                          )}
                        </div>
                      )}

                      <div
                        style={{ position: "relative" }}
                        onMouseEnter={() => setHoveredIcon("up")}
                        onMouseLeave={() => setHoveredIcon(null)}
                      >
                        <button
                          onClick={() => {
                            if (!feedbackSubmitted) {
                              setFeedbackType("up");
                              setShowFeedbackModal(true);
                            }
                          }}
                          disabled={feedbackSubmitted}
                        >
                          <ThumbsUp size={15} />
                        </button>

                        {hoveredIcon === "up" && (
                          <div style={tooltipStyle}>Helpful</div>
                        )}
                      </div>

                      <div
                        style={{ position: "relative" }}
                        onMouseEnter={() => setHoveredIcon("down")}
                        onMouseLeave={() => setHoveredIcon(null)}
                      >
                        <button
                          onClick={() => {
                            if (!feedbackSubmitted) {
                              setFeedbackType("down");
                              setShowFeedbackModal(true);
                            }
                          }}
                          disabled={feedbackSubmitted}
                        >
                          <ThumbsDown size={15} />
                        </button>

                        {hoveredIcon === "down" && (
                          <div style={tooltipStyle}>Not Helpful</div>
                        )}
                      </div>

                      {message?.table?.csvDownload && (
                        <div
                          style={{ position: "relative" }}
                          onMouseEnter={() => setHoveredIcon("download")}
                          onMouseLeave={() => setHoveredIcon(null)}
                        >
                          <button
                            onClick={() =>
                              downloadCollectionsCSV(message.table.csvDownload)
                            }
                          >
                            <ArrowDownToLine size={15} />
                          </button>

                          {hoveredIcon === "download" && (
                            <div style={tooltipStyle}>Download CSV</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(message.type === "text" || isUser) && (
              <div className="markdown-content">
                <ReactMarkdown
                  rehypePlugins={[[rehypeRaw], [rehypeSanitize, schema]]}
                >
                  {displayedText}
                </ReactMarkdown>
              </div>
            )}

            {!isUser && message.download_link && isStreamComplete && (
              <div className="download-wrapper">
                <button onClick={handleDownload}>
                  <Download /> Download CSV
                </button>
                <button onClick={handleAddFollowUp}>
                  <Plus /> Add to follow-up
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
